import type { Focus } from '@goaltree/backend/convex/lib/focus'
import { cn } from '@goaltree/ui/lib/utils'
import { ChevronRight, Flame, Layers, Mic, Mountain } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const CARDS = [
  { focus: 'habits', icon: Flame, color: '#FF8A3D', desc: true },
  { focus: 'goals', icon: Mountain, color: '#3DA9FC', desc: true },
  { focus: 'both', icon: Layers, color: '#2dd4bf', desc: false },
] as const

// Onboarding step 1: "What brings you here?", led by the coach's 90-second setup.
export function FocusStep({
  selected,
  onPick,
  onIntake,
}: {
  selected?: Focus | null
  onPick: (f: Focus) => void
  onIntake?: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-6 pt-6 pb-12 lg:pt-12">
      <div className="space-y-1">
        <h1 className="text-2xl font-black tracking-tight sm:text-3xl">
          {t('onboarding.focus.title')}
        </h1>
        <p className="text-sm text-muted-foreground">{t('onboarding.focus.subtitle')}</p>
      </div>
      {onIntake && (
        <button
          type="button"
          onClick={onIntake}
          className="group relative flex cursor-pointer items-center gap-4 overflow-hidden rounded-2xl border border-primary/50 bg-gradient-to-br from-primary/15 to-primary/5 p-4 text-left transition-all hover:scale-[1.01] hover:border-primary/80 active:scale-[0.99]"
        >
          <span className="relative shrink-0">
            <img
              src="/coach-faces/caesar.jpg"
              alt=""
              className="size-14 rounded-full object-cover ring-2 ring-primary/40"
            />
            <span className="absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Mic className="size-3.5" strokeWidth={2.5} />
            </span>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-base leading-tight font-bold text-foreground">
              {t('intake.cta')}
            </span>
            <span className="mt-0.5 block text-sm text-muted-foreground">
              {t('intake.ctaDesc')}
            </span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-primary-text transition-transform group-hover:translate-x-0.5" />
        </button>
      )}
      {onIntake && (
        <p className="-mb-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t('intake.orStepByStep')}
        </p>
      )}
      <div className="flex flex-col gap-2.5">
        {CARDS.map(({ focus, icon: Icon, color, desc }) => {
          const on = selected === focus
          return (
            <button
              key={focus}
              type="button"
              aria-pressed={on}
              onClick={() => onPick(focus)}
              className={cn(
                'group flex cursor-pointer items-center gap-4 rounded-2xl border p-4 text-left backdrop-blur-sm transition-all hover:scale-[1.01] active:scale-[0.99]',
                on
                  ? 'border-primary/70 bg-primary/10'
                  : 'border-border/60 bg-card/40 hover:border-border',
              )}
            >
              <span
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                style={{ background: `${color}22`, color }}
              >
                <Icon className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-base leading-tight font-semibold text-foreground">
                  {t(`onboarding.focus.${focus}`)}
                </span>
                {desc && (
                  <span className="mt-0.5 block text-sm text-muted-foreground">
                    {t(`onboarding.focus.${focus}Desc`)}
                  </span>
                )}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" />
            </button>
          )
        })}
      </div>
    </div>
  )
}
